import * as THREE from 'three';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';
import { getHeight } from './terrainConfig.js';
import Utils from './Utils.js';

export class PlayerController {
    constructor({ camera, renderer, sectionScene, modelRegistry, stoneFigureParams, fnToggleFigureAnimation, personHeight, speed, endPosition, gameIsActive, hideWasdIcon }) {
        this._camera = camera;
        this._renderer = renderer;
        this._sectionScene = sectionScene;
        this._modelRegistry = modelRegistry;
        this._stoneFigureParams = stoneFigureParams;
        this._fnToggleFigureAnimation = fnToggleFigureAnimation;
        this._personHeight = personHeight;
        this._speed = speed;
        this._gameIsActive = gameIsActive;
        this._hideWasdIcon = hideWasdIcon;

        this._controls = null;
        this._controlsIsLocked = false;
        this._velocity = new THREE.Vector3();

        this._moveForward = false;
        this._moveBackward = false;
        this._moveLeft = false;
        this._moveRight = false;

        this._playerCollider = new THREE.Sphere(new THREE.Vector3(0, 0, 0));
        this._playerCollider.center.set(endPosition.x, getHeight(endPosition.x, endPosition.y), endPosition.z);

        this._initControls();
        this._initTouchInput();
        this._initKeyboardInput();
    }

    get controls() { return this._controls; }
    get isLocked() { return this._controlsIsLocked; }

    _initControls() {
        this._controls = new PointerLockControls(this._camera, this._renderer.domElement);
        this._controls.pointerSpeed = 0.8;

        this._sectionScene.addEventListener('click', () => {
            if (!this._controlsIsLocked && this._gameIsActive()) {
                this._controls.lock();
                this._controlsIsLocked = true;
                this._hideWasdIcon();
                setTimeout(() => { document.querySelector('.wasd-icon').classList.add('hide') }, 10000);
            } else {
                this._controls.unlock();
                this._controlsIsLocked = false;
            }
        });
    }

    _initTouchInput() {
        let startX = 0;
        let startY = 0;
        let isTouching = false;

        this._sectionScene.addEventListener('touchstart', (event) => {
            isTouching = true;
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
        });

        this._sectionScene.addEventListener('touchmove', (e) => {
            if (!isTouching) return;
            const currentX = e.touches[0].clientX;
            const currentY = e.touches[0].clientY;

            const deltaX = currentX - startX;
            const deltaY = currentY - startX;

            const rotationSpeed = 0.005;
            this._camera.rotation.y -= deltaX * rotationSpeed;

            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
        });

        this._sectionScene.addEventListener('touchend', () => {
            isTouching = false;
        });
    }

    _initKeyboardInput() {
        const onKeyDown = (event) => {
            switch (event.code) {
                case 'ArrowUp':
                case 'KeyW':
                    this._moveForward = true;
                    break;
                case 'ArrowLeft':
                case 'KeyA':
                    this._moveLeft = true;
                    break;
                case 'ArrowDown':
                case 'KeyS':
                    this._moveBackward = true;
                    break;
                case 'ArrowRight':
                case 'KeyD':
                    this._moveRight = true;
                    break;
            }
        };

        const onKeyUp = (event) => {
            switch (event.code) {
                case 'ArrowUp':
                case 'KeyW':
                    this._moveForward = false;
                    break;
                case 'ArrowLeft':
                case 'KeyA':
                    this._moveLeft = false;
                    break;
                case 'ArrowDown':
                case 'KeyS':
                    this._moveBackward = false;
                    break;
                case 'ArrowRight':
                case 'KeyD':
                    this._moveRight = false;
                    break;
            }
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('keyup', onKeyUp);
    }

    update(deltaTime) {
        const newPosition = this._playerCollider.center.clone();

        const direction = new THREE.Vector3();
        this._controls.getDirection(direction);

        const right = new THREE.Vector3();
        right.crossVectors(direction, this._controls.getObject().up);

        this._velocity.x -= this._velocity.x * 10.0 * deltaTime;
        this._velocity.z -= this._velocity.z * 10.0 * deltaTime;

        if (this._moveForward) this._velocity.z -= this._speed * deltaTime;
        if (this._moveBackward) this._velocity.z += this._speed * deltaTime;
        if (this._moveLeft) this._velocity.x += this._speed * deltaTime;
        if (this._moveRight) this._velocity.x -= this._speed * deltaTime;

        newPosition.addScaledVector(direction, -this._velocity.z * deltaTime);
        newPosition.addScaledVector(right, -this._velocity.x * deltaTime);

        // Collision check
        const playerRadius = 5.5;
        const playerSphere = new THREE.Sphere(newPosition, playerRadius);
        const closestPoint = new THREE.Vector3();

        this._modelRegistry.forEach((data, id) => {
            if (!Utils.fnIsWithinDistance(data.position, newPosition, data.collisionRadius)) return;

            if (data.bvh) {
                if (data.name.includes('figure')) {
                    this._fnToggleFigureAnimation(this._stoneFigureParams, data.name);
                }
                data.bvh.shapecast({
                    intersectsBounds: box => box.intersectsSphere(playerSphere),
                    intersectsTriangle: tri => {
                        tri.closestPointToPoint(newPosition, closestPoint);
                        const distance = newPosition.distanceTo(closestPoint);
                        if (distance < playerRadius) {
                            const penetrationDepth = playerRadius - distance;
                            const displacement = newPosition.clone().sub(closestPoint).normalize().multiplyScalar(penetrationDepth);
                            newPosition.add(displacement);
                        }
                    }
                });
            }
        });

        this._playerCollider.center.copy(newPosition);
        this._controls.getObject().position.copy(this._playerCollider.center);

        const height = getHeight(this._playerCollider.center.x, this._playerCollider.center.z);
        this._playerCollider.center.y = height + this._personHeight;
        this._controls.getObject().position.y = this._playerCollider.center.y;
    }
}
